import React from 'react';
import clsx from 'clsx';
import { DocumentDuplicateIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { saveTemplate } from '~/components/workspaces/hooks/use-save-template';
import { DESIGN_CHOICES_LOADING } from '~/components/workspaces/lib/document-design';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';

type Method = 'convert' | 'save';
type Step = 1 | 2 | 3;

export type SaveAsTemplateButtonProps = {
  artifactContent: string;
  artifactTitle?: string;
  onSuccess?: () => void;
  onConvertRequest?: () => void;
  // External control: allows parent to auto-open the dialog at a specific step (e.g. after conversion)
  externalOpen?: boolean;
  externalInitialStep?: Step;
  externalInitialMethod?: Method;
  onExternalClose?: () => void;
  /**
   * When true, the artifact was already produced by a Convert to Template action
   * (its document.sourceFileId is set). The modal defaults to "Save as-is" and
   * shows "Recommended" on that option instead of "Convert with Cara".
   */
  artifactWasConverted?: boolean;
  /**
   * The design the document renders with, which the saved template keeps. Null, for a document
   * with no design, saves a None template; absent saves one on the agency default; and
   * DESIGN_CHOICES_LOADING holds the save until the agency's designs tell which.
   */
  designId?: string | null | typeof DESIGN_CHOICES_LOADING;
};

export const SaveAsTemplateButton: React.FC<SaveAsTemplateButtonProps> = ({
  artifactContent,
  artifactTitle,
  onSuccess,
  onConvertRequest,
  externalOpen,
  externalInitialStep,
  externalInitialMethod,
  onExternalClose,
  artifactWasConverted = false,
  designId
}) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [step, setStep] = React.useState<Step>(1);
  const [method, setMethod] = React.useState<Method>('convert');
  const [wasOpenedExternally, setWasOpenedExternally] = React.useState(false);
  const [templateTitle, setTemplateTitle] = React.useState('');
  const [templateDescription, setTemplateDescription] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const designLoading = designId === DESIGN_CHOICES_LOADING;
  // With designs on, the server takes a template with no body for a Style and refuses it.
  const needsContent = usePagedDocumentsEnabled() && !artifactContent.trim();
  // The Library editor offers a design only with both gates on; otherwise it edits a template's
  // own header, footer and type.
  const v4On = useFlag(FEATURE_FLAGS.DOCUMENT_GENERATION_V4);
  const brandOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const designsOn = v4On && brandOn;
  const [savedTemplate, setSavedTemplate] = React.useState<{ templateId: string; title: string }>();
  const prevExternalOpenRef = React.useRef(false);
  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);

  // Respond to external open requests (e.g. auto-open after Cara finishes conversion).
  // Only react on false→true so holding externalOpen true or toggling step params does not
  // re-open after close; parent must still set externalOpen false in onExternalClose when closing.
  React.useEffect(() => {
    if (externalOpen && !prevExternalOpenRef.current) {
      setStep(externalInitialStep ?? 2);
      setTemplateTitle(artifactTitle ?? '');
      setMethod(externalInitialMethod ?? 'convert');
      setWasOpenedExternally(true);
      setError(undefined);
      setSavedTemplate(undefined);
      setShowDialog(true);
    }
    prevExternalOpenRef.current = !!externalOpen;
  }, [externalOpen, externalInitialStep, externalInitialMethod, artifactTitle]);

  const handleOpen = () => {
    setStep(1);
    setTemplateTitle(artifactTitle ?? '');
    setMethod(artifactWasConverted ? 'save' : onConvertRequest ? 'convert' : 'save');
    setWasOpenedExternally(false);
    setError(undefined);
    setSavedTemplate(undefined);
    setShowDialog(true);
  };

  const handleClose = () => {
    setShowDialog(false);
    setError(undefined);
    if (wasOpenedExternally) {
      setWasOpenedExternally(false);
      onExternalClose?.();
    }
  };

  // A dialog with no conversion to start, such as one opened outside a workspace, only saves.
  const converting = method === 'convert' && !!onConvertRequest;

  const handleNext = () => {
    if (converting && onConvertRequest) {
      // Trigger Cara conversion — the building indicator takes over, dialog closes
      setShowDialog(false);
      onConvertRequest();
    } else {
      setStep(2);
    }
  };

  const handleSave = async () => {
    if (designId === DESIGN_CHOICES_LOADING) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await saveTemplate(
        artifactContent,
        templateTitle,
        templateDescription,
        visibilityEnabled ? visibilityInputFromValue(visibility) : undefined,
        designId
      );
      setSavedTemplate(result);
      setStep(3);
      onSuccess?.();
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(`Error saving template: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button type="button" color="white" onClick={handleOpen}>
        Save New Template
      </Button>

      <Dialog open={showDialog} onClose={handleClose}>
        <div className="min-w-0">
          <DialogTitle>
            {step === 1 && 'Save New Template'}
            {step === 2 && 'Name your template'}
            {step === 3 && 'Template saved'}
          </DialogTitle>
        </div>
        <DialogBody>
          {step === 1 && (
            <div className="space-y-3">
              {onConvertRequest && (
                <button
                  type="button"
                  onClick={() => setMethod('convert')}
                  className={clsx(
                    'w-full text-left rounded-lg border-2 px-4 py-3 transition-colors',
                    method === 'convert'
                      ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-white/20 bg-white dark:bg-neutral-800'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <SparklesIcon className="size-5 text-sky-500 dark:text-sky-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                          Convert with Cara
                        </span>
                        {!artifactWasConverted && <Badge color="sky">Recommended</Badge>}
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        Cara will replace variable content (names, dates, amounts) with
                        placeholders. After converting, come back here and save.
                      </p>
                      {artifactWasConverted && (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 italic">
                          This step is already done for the current version.
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              )}
              <button
                type="button"
                onClick={() => setMethod('save')}
                className={clsx(
                  'w-full text-left rounded-lg border-2 px-4 py-3 transition-colors',
                  !converting
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10'
                    : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-white/20 bg-white dark:bg-neutral-800'
                )}
              >
                <div className="flex items-start gap-3">
                  <DocumentDuplicateIcon className="size-5 text-neutral-400 dark:text-neutral-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                        Save as-is
                      </p>
                      {artifactWasConverted && <Badge color="sky">Recommended</Badge>}
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      Save the document exactly as it is.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {step === 2 && (
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Template name</Label>
                  <Input
                    type="text"
                    value={templateTitle}
                    onChange={(e) => setTemplateTitle(e.currentTarget.value)}
                    placeholder="e.g. Quote Proposal Template"
                    autoFocus
                  />
                </Field>
                <Field>
                  <Label>Description (required)</Label>
                  <Textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.currentTarget.value)}
                    placeholder="Describe what this template is used for"
                    rows={3}
                  />
                </Field>
              </FieldGroup>
              {needsContent && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  This document is empty. Write it before saving it as a template.
                </p>
              )}
              {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
            </Fieldset>
          )}

          {step === 3 && savedTemplate && (
            <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 px-4 py-3 space-y-1.5">
              <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                Next: customize in Templates
              </p>
              {designsOn ? (
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Your template is now available in any workspace and in{' '}
                  <strong>Library → Templates</strong>, where you can change its design or set it to
                  None, and choose its start and end pages.
                </p>
              ) : (
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Your template is now available in any workspace and in{' '}
                  <strong>Library → Templates</strong>. Templates let you add headers, footers,
                  start/end pages, fonts, and typography that will be applied every time this
                  template is used.
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          {step === 1 && (
            <>
              <Button type="button" color="white" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" color="sky" onClick={handleNext}>
                {converting ? 'Start Converting' : 'Next'}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              {!wasOpenedExternally && (
                <Button type="button" color="white" onClick={() => setStep(1)}>
                  Back
                </Button>
              )}
              <Button type="button" color="white" onClick={handleClose}>
                Cancel
              </Button>
              {visibilityEnabled ? (
                <VisibilitySplitButton
                  type="button"
                  value={visibility}
                  onChange={setVisibility}
                  onClick={handleSave}
                  disabled={
                    isLoading ||
                    needsContent ||
                    designLoading ||
                    !templateTitle.trim() ||
                    !templateDescription.trim()
                  }
                  title={designLoading ? 'Loading designs…' : undefined}
                  loading={isLoading}
                >
                  Save Template
                </VisibilitySplitButton>
              ) : (
                <Button
                  type="button"
                  color="sky"
                  onClick={handleSave}
                  disabled={
                    isLoading ||
                    needsContent ||
                    designLoading ||
                    !templateTitle.trim() ||
                    !templateDescription.trim()
                  }
                  title={designLoading ? 'Loading designs…' : undefined}
                  loading={isLoading}
                >
                  Save Template
                </Button>
              )}
            </>
          )}
          {step === 3 && savedTemplate && (
            <>
              <Button type="button" color="white" onClick={handleClose}>
                Done
              </Button>
              <Button
                type="button"
                color="sky"
                onClick={() => {
                  window.open(`/app/library/templates/${savedTemplate.templateId}`, '_blank');
                  handleClose();
                }}
              >
                Edit in Templates
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
